/**
 * SignalR身份信息管理器
 * 统一处理SignalR身份信息的保存、清除和重新注册
 */

class SignalRIdentityManager {
  constructor() {
    this.STORAGE_KEY = 'signalrClientInfo';
  }

  /**
   * 保存登录后的身份信息
   * @param {Object} userInfo - 用户信息
   * @param {string} userInfo.userId - 用户ID
   * @param {string} userInfo.username - 用户名
   * @param {string} userInfo.realName - 真实姓名
   * @param {Array} userInfo.roles - 用户角色
   */
  saveUserIdentity(userInfo) {
    try {
      const signalrIdentity = {
        userId: userInfo.userId?.toString() || '',
        deviceId: '', // 设备ID可以根据需要设置
        customType: 'Web', // 自定义类型标识为Web客户端
        metaData: {
          username: userInfo.username || '',
          realName: userInfo.realName || '',
          roles: userInfo.roles || [],
          loginTime: new Date().toISOString()
        },
        savedAt: new Date().toISOString()
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(signalrIdentity));
      console.log('SignalR身份信息已保存:', signalrIdentity);
      
      return signalrIdentity;
    } catch (error) {
      console.error('保存SignalR身份信息失败:', error);
      return null;
    }
  }

  /**
   * 获取已保存的身份信息
   * @returns {Object|null} 身份信息或null
   */
  getStoredIdentity() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('读取SignalR身份信息失败:', error);
    }
    return null;
  }

  /**
   * 清除身份信息
   */
  clearIdentity() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('SignalR身份信息已清除');
    } catch (error) {
      console.error('清除SignalR身份信息失败:', error);
    }
  }

  /**
   * 检查是否有有效的身份信息
   * @returns {boolean} 是否有有效身份信息
   */
  hasValidIdentity() {
    const identity = this.getStoredIdentity();
    return identity && (identity.userId || identity.deviceId || identity.customType);
  }

  /**
   * 处理登录场景
   * @param {Object} userInfo - 用户信息
   * @param {Object} signalRManager - SignalR管理器实例
   */
  async handleLogin(userInfo, signalRManager) {
    try {
      // 1. 保存身份信息到localStorage
      const identity = this.saveUserIdentity(userInfo);
      if (!identity) return;

      // 2. 如果SignalR已连接，重新注册身份信息
      if (signalRManager && signalRManager.isConnected) {
        await signalRManager.refreshIdentity();
        console.log('登录后已刷新SignalR身份信息');
      } else {
        console.log('SignalR未连接，身份信息将在下次连接时自动注册');
      }
    } catch (error) {
      console.error('处理登录时的SignalR身份信息失败:', error);
    }
  }

  /**
   * 处理退出场景
   * @param {Object} signalRManager - SignalR管理器实例
   */
  async handleLogout(signalRManager) {
    try {
      // 1. 清除身份信息
      this.clearIdentity();

      // 2. 断开SignalR连接（可选，根据需求决定）
      if (signalRManager && signalRManager.isConnected) {
        console.log('退出时保持SignalR连接，但已清除身份信息');
        // 注意：不主动断开连接，让它保持连接但无身份信息
        // 如果需要断开，可以调用：await signalRManager.close();
      }
    } catch (error) {
      console.error('处理退出时的SignalR身份信息失败:', error);
    }
  }

  /**
   * 处理页面重新加载场景
   * @param {Object} signalRManager - SignalR管理器实例
   */
  async handlePageReload(signalRManager) {
    try {
      const token = localStorage.getItem('token');
      const identity = this.getStoredIdentity();

      if (token && identity) {
        console.log('检测到用户已登录，准备恢复SignalR连接和身份');
        
        // 如果有token和身份信息，signalr-client会在连接时自动注册
        // 这里不需要额外操作，只需要确保数据接收器能正常连接
        return true;
      } else {
        console.log('用户未登录或无身份信息，跳过SignalR身份恢复');
        return false;
      }
    } catch (error) {
      console.error('处理页面重新加载时的SignalR身份信息失败:', error);
      return false;
    }
  }
}

// 导出单例实例
export const signalRIdentityManager = new SignalRIdentityManager();
export default signalRIdentityManager;
